import { FadersIcon, XIcon } from "@phosphor-icons/react";
import {
  Checkbox,
  Flex,
  Typography,
  Badge,
  ConfigProvider,
  Button,
  Dropdown,
  MenuProps,
} from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import { useState } from "react";
import "./filters.css";
import { useTranslate } from "@refinedev/core";
import { normalizeText } from "utils/text";
import { radius } from "@libertify/ui";

export const CascadingFilters = ({
  onChange,
  onClearAll,
  filters,
  options,
  optionLabels,
  skipNormalizeOptionKeys,
  itemsCount = 0,
  label,
  type,
  loading,
}: {
  onChange: (key: string, value: string) => void;
  onClearAll?: () => void;
  filters: Record<string, string[]>;
  options?: Record<string, string[]>;
  /** Optional display strings per filter group and value (e.g. language JSON values). */
  optionLabels?: Record<string, Record<string, string>>;
  /** Show raw option values (no title-case) for these groups (e.g. language codes). */
  skipNormalizeOptionKeys?: string[];
  itemsCount: number;
  label: string;
  type: "face" | "voice";
  loading?: boolean;
}) => {
  const { theme, isMobile } = useAntTheme();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const t = useTranslate();
  let menuItems: MenuProps["items"] = [];

  const getAllStringFilterValues = (
    filters: Record<string, string[]>
  ): string[] => {
    return Object.entries(filters).flatMap(([_, values]) => values);
  };
  const isFilterActive = (key: string, value: string) => {
    const currentValues = filters[key] ?? [];
    return currentValues.includes(value);
  };
  const updateFilters = (key: string, value: string) => {
    onChange(key, value);
  };

  if (options)
    menuItems = Object.entries(options).map(([filterKey, values]) => {
      const children = values.map((value) => {
        const optionText = skipNormalizeOptionKeys?.includes(filterKey)
          ? value
          : (optionLabels?.[filterKey]?.[value] ?? normalizeText(value));
        return {
          key: value,
          label: (
            <Checkbox checked={isFilterActive(filterKey, value)}>
              {optionText}
            </Checkbox>
          ),
          onClick: () => updateFilters(filterKey, value),
        };
      });
      const filterI18nLabel = type === "face" ? "FaceFilter" : "VoiceFilter";
      const i18nKey = `components.ContactWithPresenter.${filterI18nLabel}.${filterKey}`;
      const translated = t(i18nKey);
      const groupLabel =
        translated && translated !== i18nKey
          ? translated
          : normalizeText(filterKey);
      return {
        label: groupLabel,
        key: filterKey,
        children: [
          {
            type: "group",
            children,
          },
        ],
      };
    });

  const activeFilters = getAllStringFilterValues(filters);
  const activeFilterLabels = Object.entries(filters).flatMap(([key, values]) =>
    values.map((value) => optionLabels?.[key]?.[value] ?? normalizeText(value))
  );
  const activeFiltersKeys = Object.entries(filters)
    .filter(([_, values]) => values.length > 0)
    .map(([key, _]) => key);

  const menuLabel = (
    <Flex
      justify="space-between"
      style={{
        flex: "1 0 100%",
        overflow: "hidden",
      }}
    >
      <Typography.Text type="secondary" ellipsis={true}>
        {activeFilters.length === 0
          ? normalizeText(label)
          : activeFilterLabels.join(", ")}
      </Typography.Text>
      {activeFilters.length > 0 && (
        <Flex>
          {onClearAll && (
            <span
              className="ant-btn-circle"
              role="button"
              tabIndex={0}
              aria-label={t("buttons.clear")}
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearAll();
                }
              }}
              style={{
                verticalAlign: "text-top",
                height: 20,
                width: 20,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: radius.circle,
              }}
            >
              <XIcon size={12} weight="bold" />
            </span>
          )}
          <Badge
            count={itemsCount}
            style={{ marginLeft: 8 }}
            color={theme.colorTextDescription}
            showZero
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <ConfigProvider
      theme={{
        token: { motion: false }, // disable animation for simpler UI
      }}
    >
      <Dropdown
        menu={{
          items: menuItems,
          selectable: true,
          multiple: true,
          selectedKeys: activeFiltersKeys,
        }}
        trigger={["click"]}
        open={filtersOpen}
        onOpenChange={(open, info) => {
          // do not close menu when selecting a filter, to continue selecting more filters
          if (info.source !== "menu") setFiltersOpen(open);
        }}
        rootClassName="filters-dropdown"
      >
        <Button
          size="middle"
          color={activeFiltersKeys.length > 0 ? "primary" : "default"}
          variant="outlined"
          icon={<FadersIcon size={22} style={{ verticalAlign: "text-top" }} />}
          style={{
            width: isMobile ? "100%" : "calc(100% - 50px)",
            display: "flex",
            padding: "0 25px",
          }}
          loading={loading}
        >
          {menuLabel}
        </Button>
      </Dropdown>
    </ConfigProvider>
  );
};
